import { type WatchQueryFetchPolicy } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { useLoaderData, useSearchParams } from "react-router-dom";

import {
  type AuthAppContextInterface,
  type AuthAppContextState,
} from "components/root/auth-app-provider";
import { usePoliciesList, usePolicyId } from "data/ss-data-hooks";
import {
  useCommonPolicyDataLazyQuery,
  useGetPolicyLazyQuery,
} from "gql/__generated__/hooks";
import {
  type KeystoneCache,
  type KeystonePolicyInfo,
  authUser,
  logException,
  setMixpanelProfileProperties,
  setMixpanelSuperProperties,
  useFlags,
} from "utils";

export const useAuthAppData = (
  state: AuthAppContextState,
  {
    setPolicies,
    setSelectedPolicy,
    setUserBilling,
    setUserDetails,
    setUserDocuments,
    setUserInfo,
    setUserPolicyProperties,
    setUserInsuranceRep,
    setPolicy,
  }: Omit<AuthAppContextInterface, "refetchPolicy" | "refetchPolicies">,
  setIsPolicyDataReady: (isPolicyDataReady: boolean) => void
) => {
  const { retireProxy } = useFlags();
  const selectedPolicyId = usePolicyId();
  const {
    data: policies,
    isLoading: policiesLoading,
    mutate: mutatePolicies,
  } = usePoliciesList();
  const ssPolicyId =
    policies?.find((p) => p.keystonePolicyId === selectedPolicyId)
      ?.platformPolicyId ?? "";

  const email = authUser()?.email ?? "";
  const loaderData = useLoaderData() as KeystoneCache;
  const [searchParams, setSearchParams] = useSearchParams();
  const [getPolicyQuery] = useGetPolicyLazyQuery({
    context: {
      clientName: "keystone-api",
    },
  });
  const [commonPolicyDataQuery] = useCommonPolicyDataLazyQuery({
    variables: { email, policyID: ssPolicyId },
  });

  const [getPolicyIsDone, setGetPolicyIsDone] = useState(false);
  const [commonPolicyDataIsDone, setCommonPolicyIsDone] = useState(false);

  // policyFetchCount is used purely to trigger a useEffect below every time the policy changes
  // Without it, switching to a cached policy doesn't trigger it we get stuck in loading
  const [policyFetchCount, setPolicyFetchCount] = useState(0);

  // throwing an error inside these "do<query>Query" functions doesn't get caught by the error boundaries
  // So, we set this state and throw here
  const [asyncError, setAsyncError] = useState<Error | null>(null);
  useEffect(() => {
    if (asyncError) {
      logException(asyncError);
      throw asyncError;
    }
  }, [asyncError]);

  const doCommonPolicyDataQuery = useCallback(
    async (fetchPolicy: WatchQueryFetchPolicy = "cache-first") => {
      setCommonPolicyIsDone(false);
      const { data, error } = await commonPolicyDataQuery({
        variables: { policyID: ssPolicyId, email },
        fetchPolicy,
      });

      if (error) {
        setAsyncError(error);
        return;
      }
      if (retireProxy) {
        setUserBilling(loaderData?.policyDetails?.userBilling);
        setUserDetails(loaderData?.policyDetails?.userDetails);
        setUserDocuments(loaderData?.policyDetails?.userDocuments);
        setUserInfo(loaderData?.user);
        setUserPolicyProperties(
          loaderData?.policyDetails?.userPolicyProperties
        );
        setUserInsuranceRep(loaderData?.policyDetails?.userInsuranceRep);
      } else {
        setUserBilling(data?.userBilling);
        setUserDetails(data?.userDetails);
        setUserDocuments(data?.userDocuments);
        setUserInfo(data?.userBasicInfo);
        setUserPolicyProperties(data?.userPolicyProperties);
        setUserInsuranceRep(data?.userInsuranceRep);
      }

      setCommonPolicyIsDone(true);
    },
    [
      commonPolicyDataQuery,
      ssPolicyId,
      email,
      retireProxy,
      setUserBilling,
      loaderData?.policyDetails?.userBilling,
      loaderData?.policyDetails?.userDetails,
      loaderData?.policyDetails?.userDocuments,
      loaderData?.policyDetails?.userPolicyProperties,
      loaderData?.policyDetails?.userInsuranceRep,
      loaderData?.user,
      setUserDetails,
      setUserDocuments,
      setUserInfo,
      setUserPolicyProperties,
      setUserInsuranceRep,
    ]
  );

  const doGetPolicyQuery = useCallback(
    async (fetchPolicy: WatchQueryFetchPolicy = "cache-first") => {
      setGetPolicyIsDone(false);
      const { data, error } = await getPolicyQuery({
        variables: {
          id: ssPolicyId,
        },
        fetchPolicy,
      });
      if (error) {
        setAsyncError(error);
        return;
      }
      setSelectedPolicy(data?.getPolicy);
      setGetPolicyIsDone(true);
    },
    [getPolicyQuery, setSelectedPolicy, ssPolicyId]
  );

  useEffect(() => {
    if (!ssPolicyId) {
      return;
    }
    setPolicyFetchCount((prev) => prev + 1);
    doGetPolicyQuery();
    doCommonPolicyDataQuery();
  }, [doGetPolicyQuery, doCommonPolicyDataQuery, ssPolicyId]);

  const checkForPolicyInSearchParams = useCallback(
    (policies = state.policies) => {
      if (searchParams.get("policyNumber") || searchParams.get("ipId")) {
        const validPolicy = policies?.find(
          (userPolicy) =>
            userPolicy.policyId === searchParams.get("policyNumber") ||
            userPolicy.insightPolicyId === searchParams.get("ipId")
        );

        if (validPolicy) {
          setPolicy(validPolicy);
          // we only remove the qs param if it's valid so that error messages can use it
          // and because this can fire before state.policies is populated which is a "false invalid"
          setSearchParams((params) => {
            params.delete("policyNumber");
            params.delete("ipId");
            return params;
          });
          return true;
        }
      }
      return false;
    },
    [searchParams, setPolicy, setSearchParams, state.policies]
  );

  useEffect(() => {
    checkForPolicyInSearchParams();
  }, [checkForPolicyInSearchParams]);

  const doUserPolicyIDsQuery = useCallback(async () => {
    if (policiesLoading) {
      return;
    }

    if (!policies?.length) {
      setAsyncError(new Error("No policyIDs returned from ui-server"));
      return;
    }

    const mappedPolicies: KeystonePolicyInfo[] = policies.map((p) => ({
      policyId: p.platformPolicyId,
      insightPolicyId: p.keystonePolicyId,
      isCommercial: p.isCommercial ?? false,
      policyState: p.policyState,
      propertyAddress: p.propertyAddress,
    }));

    const policy = mappedPolicies.find(
      (p) => p.insightPolicyId === selectedPolicyId
    );

    if (!policy) {
      setAsyncError(new Error("No policy found"));
      return;
    }

    setPolicies(mappedPolicies);
    setMixpanelSuperProperties({
      numberOfPolicies: mappedPolicies.length,
    });
    setMixpanelProfileProperties({
      numberOfPolicies: mappedPolicies.length,
    });

    setPolicy(policy);
  }, [policiesLoading, policies, setPolicies, setPolicy]);

  const refetchPolicy = useCallback(() => {
    doGetPolicyQuery("no-cache");
    doCommonPolicyDataQuery("no-cache");
  }, [doGetPolicyQuery, doCommonPolicyDataQuery]);

  const refetchPolicies = useCallback(() => {
    mutatePolicies();
    doUserPolicyIDsQuery();
  }, [doUserPolicyIDsQuery]);

  useEffect(() => {
    doUserPolicyIDsQuery();
  }, [doUserPolicyIDsQuery]);

  useEffect(() => {
    if (!policyFetchCount) {
      return;
    }
    setIsPolicyDataReady(getPolicyIsDone && commonPolicyDataIsDone);
  }, [
    getPolicyIsDone,
    commonPolicyDataIsDone,
    setIsPolicyDataReady,
    policyFetchCount,
  ]);

  return { refetchPolicy, refetchPolicies };
};
